import { type Immutable, produce } from 'immer';
import { toast } from 'src/lib/toast';
import type { StoreApi } from 'zustand';
import { Tristate } from '../../../components/utils/tristateTask';
import type { DraftState, State } from '../../reducers';
import type { CurrentEvent } from '../events_reducers';

export type SetState = StoreApi<State>['setState'];
export type GetState = StoreApi<State>['getState'];

export const setSavingState = (set: SetState, savingState: Tristate) =>
  set(
    produce((s: DraftState) => {
      s.events.savingState = savingState;
    }),
  );

// Shows the spinner while `save` runs. A throw sets FAILURE and hands the error to `onError`.
export async function withSavingState<T, R = void>(
  set: SetState,
  save: () => Promise<T>,
  onError: (error: unknown) => R | Promise<R>,
): Promise<T | R> {
  try {
    setSavingState(set, Tristate.SPINNING);
    const result = await save();
    setSavingState(set, Tristate.NORMAL);
    return result;
  } catch (error) {
    setSavingState(set, Tristate.FAILURE);
    return onError(error);
  }
}

export const logAndToast =
  (logMessage: string, toastMessage: string) =>
  (error: unknown): undefined => {
    console.log(`${logMessage}: ${error}`);
    toast.error(toastMessage);
  };

// Stores the event with `patch` applied and reloads it in the background.
export function patchCurrentEvent(
  set: SetState,
  get: GetState,
  eventId: string,
  patch: (event: Immutable<CurrentEvent>) => Partial<CurrentEvent>,
) {
  set(
    produce((s) => {
      const event = s.events.items.get(eventId);

      s.events.items.set(eventId, {
        ...event,
        ...patch(event),
      });
    }),
  );

  get().events.actions.loadCurrentEvent(eventId);
}
